/* Ancestral Brotherhood — motion engine.
 *
 * GSAP + ScrollTrigger (+ SplitText, + Lenis on desktop). Deferred.
 *
 * HISTORY — do not undo this:
 * A page-wide velocity skew on <main> broke both pinned sections. GSAP leaves
 * an identity matrix rather than transform:none, and ANY transform makes the
 * element a containing block for position:fixed descendants. ScrollTrigger
 * pins with position:fixed, so .ab-mission and .ab-table pinned to the top of
 * <main> instead of the viewport and scrolled away off-screen — 8,696px of
 * black, 58% of the homepage, with a clean console. The skew is gone. Never
 * put a transform, filter, perspective, backdrop-filter, contain or
 * will-change:transform on <main>, <body> or any ancestor of a pinned
 * section. The ancestor guard at the bottom of this file will shout if you do.
 *
 * SECOND HISTORY — the table's lit seat:
 * Scroll and hover were written as competing OWNERS, with a one-way latch that
 * flipped on the first pointerenter. The section is pinned full-screen, so the
 * cursor is on it the whole time — the latch flipped immediately and the seat
 * sequence went inert for the rest of the pin while the scale tweens kept
 * running, so it looked half-alive rather than plainly broken. Scroll now
 * drives the lit seat ALWAYS; hover overrides transiently and hands back on
 * pointerleave. Never reintroduce an owner latch.
 *
 * Contract: nothing is hidden unless html.ab-anim is present, which only
 * happens once GSAP has booted. A watchdog forces anything still invisible
 * back on screen.
 */
(() => {
  "use strict";

  const still = matchMedia("(prefers-reduced-motion: reduce)").matches;
  const fine  = matchMedia("(hover: hover) and (pointer: fine)").matches;
  const small = matchMedia("(max-width: 900px)").matches;

  if (still || !window.gsap || !window.ScrollTrigger) return;

  gsap.registerPlugin(ScrollTrigger);
  document.documentElement.classList.add("ab-anim");

  const q  = (s, c) => (c || document).querySelector(s);
  const qa = (s, c) => Array.from((c || document).querySelectorAll(s));
  const EASE = "power3.out";

  /* Selectors ab-anim.css starts at opacity:0 AND that are revealed by a
     one-shot trigger. Deliberately EXCLUDES .ab-mission__line: those sit at
     opacity 0 between beats of the pinned creed by design, and they are
     grid-stacked in one place, so a generic watchdog would pile all five on
     top of each other. The creed has its own guard — see creedGuard below. */
  const HIDDEN = ".ab-statement__line,.ab-statement__note," +
    ".ab-call__line,.ab-call__note,.ab-trp__flag,.ab-trp__h,.ab-trp__lede," +
    ".ab-trp__proof,.ab-trp__cta,.ab-join__title,.ab-join .ab-body,.ab-form," +
    ".ab-head,.ab-lede,.ab-foot__statement,.ab-foot__body,.ab-foot__base";

  /* ------------------------------------------------ retire the old layer -- */
  const RETIRE = new Set([
    "ab-m-rise", "ab-m-img", "ab-m-para", "ab-m-line-in", "ab-mi-line",
    "ab-mi-note", "ab-wipe", "ab-hero-lift", "ab-seal-drift", "ab-trp-in",
    "ab-join-edge"
  ]);
  qa("main *, footer *").forEach((el) => {
    const names = getComputedStyle(el).animationName;
    if (!names || names === "none") return;
    if (names.split(",").some((n) => RETIRE.has(n.trim()))) {
      el.style.animation = "none";
      el.style.opacity = "";
      el.style.transform = "";
    }
  });

  /* ------------------------------------------------------- smooth scroll -- */
  if (window.Lenis && fine) {
    const lenis = new Lenis({ duration: 1.05, smoothWheel: true, syncTouch: false });
    lenis.on("scroll", ScrollTrigger.update);
    gsap.ticker.add((t) => lenis.raf(t * 1000));
    gsap.ticker.lagSmoothing(0);
    window.abLenis = lenis;
  }

  const rise = (el, vars) => gsap.fromTo(el,
    { opacity: 0, y: 26 },
    Object.assign({ opacity: 1, y: 0, duration: .9, ease: EASE }, vars || {}));

  const arrive = (sel, opts) => {
    qa(sel).forEach((el, i) => rise(el, {
      delay: (opts && opts.stagger ? opts.stagger * i : 0),
      scrollTrigger: { trigger: (opts && opts.trigger) || el, start: (opts && opts.start) || "top 82%" }
    }));
  };

  /* A pinned section held for thousands of pixels must fill the screen, or
     the hold reads as a void. svh, not vh. */
  const stage = (el) => { el.classList.add("ab-pin-stage"); return el; };

  /* ================================================================ hero == */
  const hero = q(".ab-hero");
  if (hero) {
    const display = q(".ab-hero__display", hero);

    /* Above the fold, transform only — Chromium drops opacity:0 elements from
       LCP candidacy, so a fade here is added straight onto LCP. */
    if (display && !display.closest(".ab-mask")) {
      const mask = document.createElement("span");
      mask.className = "ab-mask";
      display.parentNode.insertBefore(mask, display);
      mask.appendChild(display);
      gsap.fromTo(display, { yPercent: 116 },
        { yPercent: 0, duration: 1.25, ease: "power4.out", delay: .12 });
    }
    gsap.from(".ab-hero__lockup", { scale: .86, duration: 1.2, ease: EASE });

    gsap.to(".ab-hero__media", {
      yPercent: 12, scale: 1.14, ease: "none",
      scrollTrigger: { trigger: hero, start: "top top", end: "bottom top", scrub: .4 }
    });
    gsap.to(".ab-hero__inner", {
      yPercent: -14, opacity: .1, ease: "none",
      scrollTrigger: { trigger: hero, start: "top top", end: "bottom top", scrub: .4 }
    });
  }

  /* ====================================================== mission creed == */
  /* One composition, committed: the letter photograph becomes a full-bleed
     background layer, the heading becomes a small eyebrow at the top of the
     stage, and the creed owns the centre. The half-and-half arrangement —
     centred display type floating over a side-composed photo with the heading
     stranded off to one side — was two design systems in one frame. */
  const mission = q(".ab-mission");
  const creed   = qa(".ab-mission__line");
  let creedStage = null, creedTrigger = null;

  if (mission && creed.length && !small) {
    const sect = stage(mission.closest(".ab-section") || mission);
    sect.classList.add("ab-stage--creed");
    creedStage = sect;
    const media = q(".ab-mission__media", mission);
    if (media) gsap.set(media, { opacity: .2 });

    const words = creed.map((line) => {
      if (window.SplitText) {
        return new SplitText(line, { type: "words", wordsClass: "ab-word" }).words;
      }
      return [line];
    });

    gsap.set(creed, { opacity: 0 });
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: sect, start: "top top",
        end: "+=" + (creed.length * 45) + "%",
        pin: true, scrub: .55, anticipatePin: 1,
        invalidateOnRefresh: true
      }
    });
    creedTrigger = tl.scrollTrigger;
    creed.forEach((line, i) => {
      tl.set(line, { opacity: 1 })
        .fromTo(words[i], { yPercent: 105, opacity: 0 },
          { yPercent: 0, opacity: 1, duration: .9, stagger: .06, ease: "power3.out" });
      if (i < creed.length - 1) {
        tl.to(words[i], { yPercent: -70, opacity: 0, duration: .7, stagger: .03, ease: "power2.in" }, "+=.55")
          .set(line, { opacity: 0 });
      }
    });
    /* the background letter brightens across the whole hold, so no stretch of
       this pin is ever completely static */
    if (media) tl.to(media, { opacity: .46, ease: "none", duration: tl.duration() || 1 }, 0);
  } else {
    arrive(".ab-mission__line", { stagger: .08 });
  }

  /* The creed's own failsafe: while it is the active pinned section its stage
     must measure top ≈ 0. If not, the pin is broken and the copy is
     unreachable — unpin, reveal, say so loudly. */
  const creedGuard = () => {
    if (!creedStage || !creedTrigger || !creedTrigger.isActive) return;
    const top = creedStage.getBoundingClientRect().top;
    if (Math.abs(top) <= 4) return;
    console.error("[ab] creed pin is not holding (top=" + Math.round(top) + ") — revealing copy");
    creedTrigger.kill(false);
    creedStage.classList.remove("ab-pin-stage", "ab-stage--creed");
    gsap.set(creed, { opacity: 1, clearProps: "transform" });
    gsap.set(".ab-word", { clearProps: "all" });
  };

  /* ======================================================== round table == */
  /* Seven seats. Scroll progress owns the lit seat at all times; hover is a
     transient override that hands control back on pointerleave. No call()
     under scrub — state is derived from progress so scrubbing backwards or
     fast is always correct. Seat tweens overlap so one is always mid-flight;
     placing them at integer beats with half-length durations left half of the
     pinned scroll frozen. */
  const table = q(".ab-table");
  if (table) {
    const knights = qa(".ab-knight", table);
    const items   = qa(".ab-pillars__item", table);
    const round   = q(".ab-round", table);
    const beats   = knights.length + 1;      /* six seats plus the open seventh */
    let hovered = null;
    let scrollSeat = 0;

    const paint = (i) => {
      knights.forEach((k, n) => k.classList.toggle("is-lit", n === i));
      items.forEach((p, n) => p.classList.toggle("is-active", n === i));
      if (round) round.classList.toggle("is-open-lit", i >= knights.length);
    };

    const indexOf = (el) => {
      const k = knights.indexOf(el);
      if (k > -1) return k;
      return items.indexOf(el);
    };

    table.addEventListener("pointerover", (e) => {
      const el = e.target.closest(".ab-knight, .ab-pillars__item");
      if (!el) return;
      const i = indexOf(el);
      if (i < 0) return;
      hovered = i;
      paint(i);
    }, { passive: true });

    table.addEventListener("pointerleave", () => {
      hovered = null;
      paint(scrollSeat);
    }, { passive: true });

    if (!small) {
      const sect = stage(table.closest(".ab-section") || table);
      const span = knights.length * 0.85;
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sect, start: "top top",
          end: "+=" + (beats * 40) + "%",
          pin: true, scrub: .5, anticipatePin: 1,
          invalidateOnRefresh: true,
          onUpdate: (self) => {
            scrollSeat = Math.min(beats - 1, Math.floor(self.progress * beats));
            if (hovered === null) paint(scrollSeat);
          }
        }
      });
      /* the table rights itself across the ENTIRE hold — guarantees there is
         never a stretch of this pin with nothing moving */
      if (round) {
        gsap.set(round, { rotate: -7, scale: .94, transformOrigin: "50% 50%" });
        tl.to(round, { rotate: 0, scale: 1, ease: "none", duration: span + 1.4 }, 0);
      }
      knights.forEach((k, i) => {
        tl.fromTo(k, { scale: .68 },
          { scale: 1, duration: 1, ease: "back.out(2.2)" }, i * 0.85);
      });
      tl.from(".ab-sword", { scale: .6, opacity: 0, duration: 1, ease: "back.out(2)" }, span)
        .from(".ab-table__cta", { opacity: 0, y: 22, duration: .8, ease: EASE }, span + .6);
    } else {
      arrive(".ab-pillars__item", { stagger: .06 });
    }
  }

  /* ========================================================== statement == */
  const ROMAN = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII"];
  qa(".ab-statement__lines").forEach((group) => {
    qa(".ab-statement__line", group).forEach((line, i) => {
      let wax = line.previousElementSibling;
      if (!wax || !wax.classList.contains("ab-wax")) {
        wax = document.createElement("span");
        wax.className = "ab-wax";
        wax.setAttribute("aria-hidden", "true");   /* decoration, not copy */
        wax.textContent = ROMAN[i] || "";
        line.parentNode.insertBefore(wax, line);
      }
      const tl = gsap.timeline({ scrollTrigger: { trigger: line, start: "top 84%" } });
      tl.fromTo(wax, { scale: 2, opacity: 0, rotate: -24 },
                     { scale: 1, opacity: 1, rotate: 0, duration: .55, ease: "power4.out" })
        .fromTo(line, { clipPath: "inset(0 100% 0 0)", opacity: 1 },
                      { clipPath: "inset(0 0% 0 0)", duration: .85, ease: EASE }, "-=.32");
    });
  });
  arrive(".ab-statement__note");

  /* =============================================================== call == */
  arrive(".ab-call__line", { stagger: .12 });
  arrive(".ab-call__note");
  gsap.to(".ab-clasp__media", {
    yPercent: 10, ease: "none",
    scrollTrigger: { trigger: ".ab-clasp", start: "top bottom", end: "bottom top", scrub: .4 }
  });
  gsap.from(".ab-clasp__payoff", {
    opacity: 0, scale: .92, duration: 1, ease: "power4.out",
    scrollTrigger: { trigger: ".ab-clasp", start: "top 70%" }
  });

  /* ====================================================== training promo == */
  /* NO pointer tilt here. A card tipping a few degrees under the mouse reads
     as unstable, not premium. The staggered arrival is the whole effect. */
  const trp = q(".ab-trp");
  if (trp) {
    const tl = gsap.timeline({ scrollTrigger: { trigger: trp, start: "top 74%" } });
    tl.set(".ab-trp__proof", { opacity: 1 }, 0)
      .fromTo(".ab-trp__flag",  { opacity: 0, y: 18 }, { opacity: 1, y: 0, duration: .6, ease: EASE })
      .fromTo(".ab-trp__h",     { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: .9, ease: EASE }, "-=.35")
      .fromTo(".ab-trp__lede",  { opacity: 0, y: 24 }, { opacity: 1, y: 0, duration: .8, ease: EASE }, "-=.6")
      .fromTo(".ab-trp__proof li", { opacity: 0, y: 16 }, { opacity: 1, y: 0, duration: .6, stagger: .09, ease: EASE }, "-=.5")
      .fromTo(".ab-trp__cta",   { opacity: 0, y: 18 }, { opacity: 1, y: 0, duration: .7, ease: EASE }, "-=.35");
  }

  /* =============================================================== join == */
  arrive(".ab-join__title");
  arrive(".ab-join .ab-body", { start: "top 84%" });
  arrive(".ab-form", { start: "top 86%" });
  gsap.to(".ab-join__media img", {
    yPercent: 8, ease: "none",
    scrollTrigger: { trigger: ".ab-join", start: "top bottom", end: "bottom top", scrub: .5 }
  });

  /* ============================================================= footer == */
  arrive(".ab-foot__statement");
  arrive(".ab-foot__body", { start: "top 88%" });
  arrive(".ab-foot__base", { start: "top 92%" });
  gsap.to(".ab-foot__seal", {
    rotate: 34, ease: "none",
    scrollTrigger: { trigger: ".ab-foot", start: "top bottom", end: "bottom bottom", scrub: .6 }
  });

  arrive(".ab-head");
  arrive(".ab-lede", { start: "top 84%" });

  /* ============================================================ cursor == */
  if (fine) {
    const cur = document.createElement("div");
    cur.className = "ab-cur";
    cur.setAttribute("aria-hidden", "true");
    cur.innerHTML = '<span class="ab-cur__label"></span>';
    document.body.appendChild(cur);
    const label = q(".ab-cur__label", cur);
    gsap.set(cur, { opacity: 1 });
    const cx = gsap.quickTo(cur, "x", { duration: .36, ease: "power3" });
    const cy = gsap.quickTo(cur, "y", { duration: .36, ease: "power3" });
    addEventListener("pointermove", (e) => { cx(e.clientX); cy(e.clientY); }, { passive: true });

    const mark = (els, cls, text) => qa(els).forEach((el) => {
      el.addEventListener("pointerenter", () => {
        cur.classList.add(cls);
        if (text) { label.textContent = text; cur.classList.add("is-label"); }
      });
      el.addEventListener("pointerleave", () => {
        cur.classList.remove(cls, "is-label");
        label.textContent = "";
      });
    });
    mark("a, button, .ab-input", "is-on");
    mark(".ab-pillar, .ab-knight", "is-seat", "TAKE YOUR SEAT");
    mark(".ab-clasp, .ab-mission__media, .ab-round", "is-media");
    mark(".ab-trp__cta", "is-on", "ENTER THE HALL");
  }

  /* ========================================================== magnetic == */
  /* Offset capped so a button can never be pulled off its own hit target. */
  if (fine) {
    qa(".ab-btn, .ab-trp__cta").forEach((el) => {
      const mx = gsap.quickTo(el, "x", { duration: .5, ease: "power3" });
      const my = gsap.quickTo(el, "y", { duration: .5, ease: "power3" });
      el.addEventListener("pointermove", (e) => {
        const r = el.getBoundingClientRect();
        mx(gsap.utils.clamp(-14, 14, (e.clientX - (r.left + r.width / 2)) * .26));
        my(gsap.utils.clamp(-10, 10, (e.clientY - (r.top + r.height / 2)) * .34));
      }, { passive: true });
      el.addEventListener("pointerleave", () => { mx(0); my(0); });
    });
  }

  /* ====================================================== ancestor guard == */
  const auditPins = () => {
    ScrollTrigger.getAll().filter((t) => t.pin).forEach((t) => {
      let el = t.pin.parentElement;
      while (el && el !== document.documentElement) {
        const cs = getComputedStyle(el);
        if (cs.transform !== "none" || cs.filter !== "none" ||
            cs.perspective !== "none" || cs.backdropFilter !== "none" ||
            /transform/.test(cs.willChange) || cs.contain !== "none") {
          console.error("[ab] pin ancestor creates a containing block — pin will break:", el, cs.transform, cs.contain);
        }
        el = el.parentElement;
      }
    });
  };

  /* ============================================================ watchdog == */
  const seen = new WeakMap();
  let ticks = 0;
  const watchdog = setInterval(() => {
    creedGuard();
    const now = performance.now();
    qa(HIDDEN).forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.bottom < 0 || r.top > innerHeight * 0.95) return;
      if (parseFloat(getComputedStyle(el).opacity) >= 0.9) return;
      const first = seen.get(el);
      if (!first) { seen.set(el, now); return; }
      if (now - first > 1500) {
        gsap.set(el, { opacity: 1, clearProps: "transform,clipPath" });
        console.warn("[ab] watchdog revealed:", el);
      }
    });
    if (++ticks > 40) clearInterval(watchdog);
  }, 800);

  addEventListener("load", () => { ScrollTrigger.refresh(); auditPins(); });
  if (document.fonts) document.fonts.ready.then(() => ScrollTrigger.refresh());
  ScrollTrigger.addEventListener("refresh", () => { ticks = 0; });
})();
